import { Responsive, WidthProvider } from 'react-grid-layout/legacy'
import type { Layout, LayoutItem } from 'react-grid-layout'
import type { PivotWidgetV1, WidgetLayout } from './dashboardStorage'

/**
 * react-grid-layout config shared by Panel (NewDashboard.tsx, tenant-wide
 * widgets) and /home's pinned-widget window (Dashboard.tsx, per-user
 * selection) — same drag/resize grid in both places, not a second
 * implementation of the same layout math.
 */
export const ResponsiveGridLayout = WidthProvider(Responsive)

export const GRID_BREAKPOINT_DESKTOP = 'lg'
export const GRID_BREAKPOINTS = { lg: 900, xs: 0 }
export const GRID_COLS = { lg: 12, xs: 1 }
export const GRID_ROW_HEIGHT = 40
export const GRID_DEFAULT_HEIGHT_ROWS = 8

// Floor on how small a widget can get, in either axis — without this, a
// user (or an accidental resize) can shrink a widget down to something with
// no usable content area at all.
export const GRID_MIN_W = 3
export const GRID_MIN_H = 4

export const legacyWidthToGridSpan = (width: PivotWidgetV1['width']): number => {
  if (width === 'narrow') return 4
  if (width === 'half') return 6
  return 12
}

export const buildLayoutItem = (
  id: string,
  index: number,
  layout: WidgetLayout | undefined,
  width: PivotWidgetV1['width'] | undefined
): LayoutItem => {
  if (layout) {
    return {
      i: id,
      x: layout.x,
      y: layout.y,
      w: layout.w,
      h: layout.h,
      minW: GRID_MIN_W,
      minH: GRID_MIN_H
    }
  }
  // No explicit layout yet (never dragged/resized) — auto-place in array
  // order. The large per-index y gap is a standard react-grid-layout trick:
  // vertical compaction packs everything upward on first render, so this
  // just guarantees widgets land in array order rather than overlapping at
  // y:0.
  return {
    i: id,
    x: 0,
    y: index * 1000,
    w: legacyWidthToGridSpan(width),
    h: GRID_DEFAULT_HEIGHT_ROWS,
    minW: GRID_MIN_W,
    minH: GRID_MIN_H
  }
}

export const gridLayoutsEqual = (a: WidgetLayout | undefined, b: WidgetLayout): boolean => (
  a !== undefined && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h
)

// Shared by both screens' onDragStop/onResizeStop handlers — see
// NewDashboard.tsx's `handleGridInteractionStop` docstring for why those
// events (not onLayoutChange) are the right ones to persist from.
export const computeChangedLayoutEntries = (
  newLayout: Layout,
  currentLayoutById: (id: string) => WidgetLayout | undefined
): Array<{ id: string; layout: WidgetLayout }> => (
  newLayout
    .filter((item) => !gridLayoutsEqual(currentLayoutById(item.i), { x: item.x, y: item.y, w: item.w, h: item.h }))
    .map((item) => ({ id: item.i, layout: { x: item.x, y: item.y, w: item.w, h: item.h } }))
)
